import { useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  ChevronDown,
  ChevronRight,
  CloudCheck,
  CloudOff,
  LoaderCircle,
  RefreshCw,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useMe } from "@/lib/auth";
import { cn } from "@/lib/cn";
import { useEstadoConexion } from "@/lib/online-status";
import { type OutboxMutation, removeMutation, retryMutation } from "@/lib/outbox";
import { traducirEstadoServidor } from "@/lib/sync-estado";
import { flushOutbox } from "@/lib/sync-runner";
import { claseEstado, describeIntencion, etiquetaEstado, nombreObra } from "./estado";
import { useOutboxMutations } from "./useOutbox";

// formatFecha da un instante (ms Unix) en formato local es-ES (día y hora), para el detalle.
function formatFecha(ms: number): string {
  const fecha = new Date(ms);
  if (Number.isNaN(fecha.getTime())) {
    return "—";
  }
  return fecha.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * ConflictCenterPage es el centro de sincronización (bandeja) de F4 (#53): reúne el trabajo hecho
 * sin conexión que aún no se ha confirmado con el servidor. Separa lo que REQUIERE ATENCIÓN del
 * técnico (mutaciones en conflicto/rechazo/bloqueo) de lo que está solo PENDIENTE de enviar. Por
 * cada conflicto muestra la intención del técnico frente al estado del servidor y ofrece descartar,
 * reintentar o ver el detalle —sin auto-resolver nada: el servidor es la única autoridad—.
 */
export function ConflictCenterPage() {
  const estadoConexion = useEstadoConexion();
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  const { mutaciones, cargando } = useOutboxMutations();
  const [sincronizando, setSincronizando] = useState(false);

  const porResolver = mutaciones.filter((m) => m.status !== "pending");
  const pendientes = mutaciones.filter((m) => m.status === "pending");
  const vacio = mutaciones.length === 0;

  // El GATE es binario y fail-closed (#237): todo lo que no sea red CONFIRMADA cuenta como sin red
  // —la ventana «sin confirmar» del arranque incluida—, que es exactamente el `useOnlineStatus()`
  // anterior. El AVISO, en cambio, distingue los dos motivos (#339 D2): esta pantalla NO es un
  // modal, es a la que se viene precisamente a entender el estado de la sincronización, así que
  // callarse sería peor que hablar; durante la ventana dice lo mismo que la píldora de la
  // cabecera, que se está comprobando, en vez de afirmar una falta de red que aún no se sabe.
  const sinConexion = estadoConexion !== "online";
  const conexionSinConfirmar = estadoConexion === "sin_confirmar";

  async function sincronizarAhora() {
    setSincronizando(true);
    try {
      await flushOutbox(queryClient);
    } finally {
      setSincronizando(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col">
          <h1 className="font-bold text-2xl text-slate-900 tracking-tight">Sincronización</h1>
          <p className="text-base text-slate-500">
            Trabajo hecho sin conexión pendiente de confirmar con el servidor.
          </p>
        </div>
        {!sinConexion && pendientes.length > 0 ? (
          <Button onClick={() => void sincronizarAhora()} disabled={sincronizando}>
            {sincronizando ? (
              <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : (
              <RefreshCw className="h-5 w-5" aria-hidden="true" />
            )}
            Sincronizar ahora
          </Button>
        ) : null}
      </div>

      {sinConexion ? (
        <p
          className={cn(
            "flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm",
            conexionSinConfirmar
              ? "border-slate-200 bg-slate-50 text-slate-500"
              : "border-amber-200 bg-amber-50 text-amber-800",
          )}
        >
          {conexionSinConfirmar ? (
            <RefreshCw className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
          ) : (
            <CloudOff className="h-4 w-4 shrink-0" aria-hidden="true" />
          )}
          {conexionSinConfirmar
            ? "Comprobando conexión…"
            : "Sin conexión: el trabajo pendiente se enviará automáticamente al reconectar."}
        </p>
      ) : null}

      {cargando ? (
        <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
          <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
          Cargando…
        </div>
      ) : null}

      {!cargando && vacio ? (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 border-dashed bg-white px-4 py-12 text-center text-slate-500">
          <CloudCheck className="h-8 w-8 text-emerald-500" aria-hidden="true" />
          <span>No hay nada pendiente de sincronizar. Todo está al día.</span>
        </div>
      ) : null}

      {porResolver.length > 0 ? (
        <section className="flex flex-col gap-3">
          <h2 className="flex items-center gap-2 font-bold text-slate-900 text-xl tracking-tight">
            <TriangleAlert className="h-5 w-5 text-amber-500" aria-hidden="true" />
            Requieren tu atención ({porResolver.length})
          </h2>
          <ul className="flex flex-col gap-3">
            {porResolver.map((m) => (
              <ItemMutacion
                key={m.seq}
                mutacion={m}
                online={!sinConexion}
                obra={nombreObra(me?.obras, m.idObra)}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {pendientes.length > 0 ? (
        <section className="flex flex-col gap-3">
          <h2 className="font-bold text-slate-900 text-xl tracking-tight">
            Pendientes de enviar ({pendientes.length})
          </h2>
          <ul className="flex flex-col gap-3">
            {pendientes.map((m) => (
              <ItemMutacion
                key={m.seq}
                mutacion={m}
                online={!sinConexion}
                obra={nombreObra(me?.obras, m.idObra)}
              />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/**
 * ItemMutacion es una tarjeta de la bandeja: la intención del técnico frente al estado del
 * servidor, con las acciones Descartar / Reintentar (solo si no está pendiente) / Ver detalle.
 * El detalle expone el payload crudo, el código de error y los intentos para diagnóstico. `obra`
 * es el nombre de la obra a la que pertenece la mutación (la cola es una FIFO global multi-obra),
 * ya resuelto por el llamador desde `me.obras` con degradación a «Obra #N» si es desconocida.
 */
function ItemMutacion({
  mutacion,
  online,
  obra,
}: {
  mutacion: OutboxMutation;
  online: boolean;
  obra: string;
}) {
  const queryClient = useQueryClient();
  const [abierto, setAbierto] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const seq = mutacion.seq;
  const requiereAtencion = mutacion.status !== "pending";

  async function descartar() {
    if (seq === undefined) {
      return;
    }
    setOcupado(true);
    try {
      await removeMutation(seq);
    } finally {
      setOcupado(false);
    }
  }

  async function reintentar() {
    if (seq === undefined) {
      return;
    }
    setOcupado(true);
    try {
      await retryMutation(seq);
      // Si hay red, se intenta enviar de inmediato; offline, quedará pendiente hasta reconectar.
      if (online) {
        await flushOutbox(queryClient);
      }
    } finally {
      setOcupado(false);
    }
  }

  return (
    <li
      className={cn(
        "flex flex-col gap-3 rounded-2xl border p-4",
        requiereAtencion ? "border-amber-200 bg-amber-50/60" : "border-slate-200 bg-white",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-slate-900">{describeIntencion(mutacion)}</span>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 font-semibold text-xs",
                claseEstado(mutacion.status),
              )}
            >
              {etiquetaEstado(mutacion.status)}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600 text-xs">
              <Building2 className="h-3 w-3 shrink-0" aria-hidden="true" />
              {obra}
            </span>
          </div>
          <span className="text-slate-600 text-sm">
            <span className="text-slate-400">En el servidor: </span>
            {traducirEstadoServidor(mutacion.status, mutacion.errorCode)}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setAbierto((v) => !v)}
          aria-expanded={abierto}
        >
          {abierto ? (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          )}
          Ver detalle
        </Button>
        {requiereAtencion ? (
          <Button variant="outline" size="sm" onClick={() => void reintentar()} disabled={ocupado}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Reintentar
          </Button>
        ) : null}
        <Button variant="outline" size="sm" onClick={() => void descartar()} disabled={ocupado}>
          <Trash2 className="h-4 w-4" aria-hidden="true" />
          Descartar
        </Button>
      </div>

      {abierto ? (
        <dl className="flex flex-col gap-1.5 rounded-xl border border-slate-200 bg-white p-3 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">Operación</dt>
            <dd className="text-right font-medium text-slate-900 tabular-nums">
              {mutacion.entity}/{mutacion.op}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">Creada</dt>
            <dd className="text-right text-slate-900">{formatFecha(mutacion.createdAt)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">Intentos</dt>
            <dd className="text-right text-slate-900 tabular-nums">{mutacion.attempts}</dd>
          </div>
          {mutacion.errorCode ? (
            <div className="flex justify-between gap-3">
              <dt className="text-slate-500">Código</dt>
              <dd className="text-right font-mono text-slate-900 text-xs">{mutacion.errorCode}</dd>
            </div>
          ) : null}
          <div className="flex flex-col gap-1">
            <dt className="text-slate-500">Datos</dt>
            <dd>
              <pre className="overflow-x-auto rounded-lg bg-slate-50 p-2 font-mono text-slate-700 text-xs">
                {JSON.stringify(mutacion.payload, null, 2)}
              </pre>
            </dd>
          </div>
        </dl>
      ) : null}
    </li>
  );
}
